import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { differenceInDays } from 'date-fns'
import format from 'date-fns/format/index'
import pluralize from 'pluralize'
import { isEmpty, isNil, debounce, set, cloneDeep } from 'lodash'
import { IconInfoCircle } from '@tabler/icons-react'

import { Checkbox } from '@/components/forms/Checkbox'
import { TextInput } from '@/components/forms/TextInput'
import { Divider } from '@/components/dividers/Divider'
import { Loader } from '@/components/loaders/Loader'
import { toast } from '@/components/toasts/toast.helpers'
import { getOrganizationOptions } from '@/utils/helpers'
import { MultiSelect } from '@/components/forms/MultiSelect'
import { fetchScorecardsByOrg, fetchAgentsByOrg } from '@/reducers/callSearch/callSearch.actions'
import { FilterTypeSelect } from '@/components/filters/types/FilterTypeSelect'
import { FilterForm } from '@/components/filters/FilterForm'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Card } from '@/components/cards/Card'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { clearData, fetchCallsForDataCleanup } from '@/reducers/dataCleanup/dataCleanup.actions'
import { setFilters, setData } from '@/reducers/dataCleanup/dataCleanup.redux'
import { Button } from '@/components/buttons/Button'
import { Banner } from '@/components/banners/Banner'
import { PageHeader } from '@/components/headers/PageHeader'
import { Menu } from '@/components/navigation/Menu'
import { FormField } from '@/components/forms/FormField'
import { FormFieldGrid } from '@/components/forms/FormFieldGrid'

const CALL_IDS_TAB = 'call_ids'
const FILTERS_TAB = 'filters'

const DataCleanupPage = () => {
  const {
    organizations,
    currentUser,
    dataCleanup: { filters, data, loading },
    callSearch: { data: callSearchData, loading: callSearchLoading },
  } = useSelector((state) => state)

  const dispatch = useDispatch()

  const [dateInvalid, setDateInvalid] = useState(false)
  const [callIds, setCallIds] = useState('')
  const [agentSearchQuery, setAgentSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState(CALL_IDS_TAB)
  useEffect(() => {
    if (filters.organizationId) {
      dispatch(fetchScorecardsByOrg(filters.organizationId))
      dispatch(fetchAgentsByOrg(filters.organizationId))
      dispatch(setFilters({ agents: [] }))
      dispatch(setFilters({ scorecards: [] }))
    }
  }, [filters.organizationId])

  useEffect(() => {
    if (activeTab === CALL_IDS_TAB) {
      const updateCallId = (ids) => {
        if (typeof ids !== 'string' || isEmpty(ids)) {
          dispatch(setData({ calls: [] }))
          return
        }
        const calls = ids.split(',').map((callId) => ({ call_id: callId.trim() }))
        dispatch(setData({ calls }))
      }
      const debounced = debounce(updateCallId, 400)
      debounced(callIds)
      return () => debounced.cancel()
    }
    return undefined
  }, [callIds, activeTab])

  const scorecardOptions = (callSearchData.scorecardConfigs || [])
    .filter((scorecard) => scorecard?.active === true && scorecard?.deleted === false)
    .map((scorecard) => ({
      key: scorecard?.sid,
      label: scorecard?.name,
      value: scorecard?.sid,
    }))

  const fetchCallsFromFilters = () => {
    if (activeTab === FILTERS_TAB) {
      const body = {
        agent_ids: filters.agents.map((agent) => agent.value),
        scorecard_config_scids: filters.scorecards.map((scorecard) => scorecard.value),
        organization_id: filters.organizationId,
        start_date: filters.startDate,
        end_date: filters.endDate,
      }
      dispatch(fetchCallsForDataCleanup(body))
    }
  }

  useEffect(() => {
    fetchCallsFromFilters()
  }, [filters, activeTab])

  const handleSubmit = () => {
    dispatch(setData({ modalOpen: true }))
  }
  const handleUpdateRange = ({ selection }) => {
    const currentStartDate = format(selection.startDate, "yyyy-MM-dd'T00:00:00'xxx")
    const currentEndDate = format(selection.endDate, "yyyy-MM-dd'T23:59:59'xxx")

    const datesDiff = differenceInDays(selection.endDate, selection.startDate)
    const maxDateRange = 62
    datesDiff > maxDateRange ? setDateInvalid(true) : setDateInvalid(false)
    dispatch(setFilters({ startDate: currentStartDate }))
    dispatch(setFilters({ endDate: currentEndDate }))
  }
  const handleUpdateFilters = (section, value) => {
    dispatch(setFilters({ [section]: value }))
  }

  const handleToggle = (path, value) => () => {
    const newState = cloneDeep(data.flagsToClear)
    set(newState, path, value)
    dispatch(setData({ flagsToClear: newState }))
  }

  const getGroupValues = (groupName) => {
    return groupName === 'all'
      ? Object.values(data.flagsToClear).flatMap(Object.values)
      : Object.values(data.flagsToClear[groupName])
  }
  const areNoFlagsSelected = useMemo(() => {
    return getGroupValues('all').every((v) => !v)
  }, [data.flagsToClear])

  const isIndeterminate = (groupName) => {
    const values = getGroupValues(groupName)
    return values.some(Boolean) && values.some((v) => !v)
  }

  const isEveryElementInGroupTrue = (groupName) => {
    return getGroupValues(groupName).every(Boolean)
  }

  const handleToggleScorecards = (optionalValue = null) => {
    const newState = { ...data.flagsToClear }
    const newValue = !isNil(optionalValue) ? optionalValue : !data.flagsToClear.callScore.scorecards
    const toUpdate = ['callScore', 'coaching', 'compliance']

    toUpdate.forEach((groupName) => {
      Object.keys(newState[groupName]).forEach((key) => {
        newState[groupName][key] = newValue
      })
    })
    dispatch(setData({ flagsToClear: newState }))
  }

  const handleToggleGroup = (groupName) => () => {
    const newState = { ...data.flagsToClear }

    const newValue = isIndeterminate(groupName) ? true : !isEveryElementInGroupTrue(groupName)

    if (groupName === 'all') {
      Object.keys(newState).forEach((section) => {
        Object.keys(newState[section]).forEach((key) => {
          newState[section][key] = newValue
        })
      })
    } else if (groupName === 'callScore') {
      handleToggleScorecards(newValue)
    } else {
      Object.keys(newState[groupName]).forEach((key) => {
        newState[groupName][key] = newValue
      })
    }
    dispatch(setData({ flagsToClear: newState }))
  }

  const handleSubmitDeletion = async () => {
    const requestData = {
      orgId: filters.organizationId,
      audio: data.flagsToClear.callMedia.audio,
      video: data.flagsToClear.callMedia.video,
      transcript: data.flagsToClear.callMedia.transcript,
      summary: data.flagsToClear.callMedia.summary,
      qaScore: data.flagsToClear.callScore.scorecards,
      qaFeedback: data.flagsToClear.callScore.qaFeedback,
      complianceTask: data.flagsToClear.compliance.complianceTask,
      complianceNote: data.flagsToClear.compliance.complianceNote,
      coachingTask: data.flagsToClear.coaching.coachingTask,
      coachingNote: data.flagsToClear.coaching.coachingNote,
      callIds: data.calls.map((call) => call.call_id),
      scorecard_sids: filters.scorecardConfigsToDelete.map((scorecard) => scorecard.value),
    }
    // if every element is false
    if (!isIndeterminate('all') && !isEveryElementInGroupTrue('all')) {
      toast.error('You must specify at least one data type to delete')
      return
    }
    if (isEmpty(data.calls)) {
      toast.error('You must specify at least one call to delete data from')
      return
    }
    dispatch(setData({ deletionError: null }))
    try {
      await dispatch(clearData(requestData))
    } catch (error) {
      dispatch(
        setData({
          deletionError: { message: error.message || 'An unknown error occurred' },
        })
      )
    }
  }

  const tabItems = [
    {
      accessor: CALL_IDS_TAB,
      label: 'Enter Comma Separated Call IDs',
      active: activeTab === CALL_IDS_TAB,
      onClick: () => setActiveTab(CALL_IDS_TAB),
    },
    {
      accessor: FILTERS_TAB,
      label: 'Use filters to scan calls',
      active: activeTab === FILTERS_TAB,
      onClick: () => setActiveTab(FILTERS_TAB),
    },
  ]

  return (
    <>
      <PageHeader
        title="Data Cleanup"
        actions={
          <FilterTypeSelect
            isClearable={filters.organizationId !== currentUser.organizationid}
            schema={{
              accessor: 'organizationId',
              label: 'Organization',
              options: getOrganizationOptions(organizations),
            }}
            value={filters.organizationId}
            handleUpdateFilter={(value) => {
              dispatch(setFilters({ organizationId: value }))
            }}
            updateSearch={() => {}}
          />
        }
      />
      <Menu items={tabItems} className="mb" />

      <Card>
        {activeTab === CALL_IDS_TAB && (
          <FormFieldGrid>
            <FormField span={6}>
              <label>Enter Call IDs</label>
              <TextInput
                placeholder="Enter comma separated call IDs e.g. 12345,67890"
                value={callIds}
                onChange={(event) => setCallIds(event.target.value)}
              />
            </FormField>
          </FormFieldGrid>
        )}
        {activeTab === FILTERS_TAB && (
          <>
            <FilterForm small sticky={false}>
              <FormField data-testid="date-picker">
                <label>Date Range</label>
                <DateRangePickerReusable
                  onChange={handleUpdateRange}
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  error={dateInvalid}
                  allow120Days={false}
                  saveOrCloseOnClickPreset
                />
                {dateInvalid && (
                  <ErrorMessage
                    content="Date range must be 60 days or less"
                    data-testid="datepicker-error"
                  />
                )}
              </FormField>
              <FormField data-testid="agents-filter">
                <label>Agents</label>
                <MultiSelect
                  isSelectAll
                  placeholderPill="All Agents"
                  label="Agent"
                  options={
                    !agentSearchQuery && callSearchData.agents.length > 1000
                      ? []
                      : callSearchData.agents
                  }
                  optionsLength={callSearchData.agents.length}
                  loading={callSearchLoading.agents}
                  value={filters.agents}
                  onInputChange={(inputValue) => {
                    setAgentSearchQuery(inputValue)
                  }}
                  onChange={(value) => {
                    handleUpdateFilters('agents', value)
                  }}
                  noOptionsMessage={() =>
                    agentSearchQuery ? 'No results found' : 'Begin typing to search...'
                  }
                />
              </FormField>
              <FormField data-testid="scorecard-config-filter">
                <label id="scorecards">Scorecards</label>
                <MultiSelect
                  isSelectAll
                  placeholderPill="No Scorecards selected"
                  options={scorecardOptions}
                  aria-labelledby="scorecards"
                  loading={callSearchLoading.scorecards}
                  value={filters.scorecards}
                  onChange={(value) => handleUpdateFilters('scorecards', value)}
                />
              </FormField>
            </FilterForm>
            <div className="mt flex-align-center small-gap">
              {loading.calls && <Loader />}
              <label>
                {loading.calls
                  ? 'Loading calls...'
                  : `Found ${data.calls?.length} ${pluralize('calls', data.calls?.length)} matching the filters`}
              </label>
            </div>
          </>
        )}
      </Card>
      {data.modalOpen && (
        <BasicModal
          data-testid="data-deletion-modal"
          title="Data Deletion"
          showCloseButton={false}
          canCloseOnDimmerClick={false}
          show={data.modalOpen}
          size="large"
        >
          {data.deletionError && (
            <Banner
              critical
              icon={<IconInfoCircle data-testid="banner-icon" />}
              data-testid="deletion-error-banner"
            >
              <strong>Organization Mismatch</strong>
              <p>{data.deletionError.message}</p>
              <p>
                <strong>
                  Please verify that you have selected the correct organization and that all call
                  IDs belong to it.
                </strong>
              </p>
            </Banner>
          )}

          {!data.deletionError && (
            <label>
              This action will permanently delete selected data for {data.calls.length}{' '}
              {pluralize('calls', data.calls.length)}. Deleted data cannot be retrieved later.
            </label>
          )}
          <div className="modal-footer align-right">
            <Button
              data-testid="cancel-button"
              onClick={() => {
                dispatch(setData({ modalOpen: false, deletionError: null }))
              }}
            >
              {data.deletionError ? 'Close' : 'Cancel'}
            </Button>
            {!data.deletionError && (
              <Button
                data-testid="submit-deletion-button"
                brand
                onClick={handleSubmitDeletion}
                loading={loading.clearData}
              >
                Submit
              </Button>
            )}
          </div>
        </BasicModal>
      )}
      <Card title="Select items to cleanup">
        <div>
          <FormField>
            <Checkbox
              label="Select All"
              onChange={handleToggleGroup('all')}
              indeterminate={isIndeterminate('all')}
              checked={isEveryElementInGroupTrue('all')}
            />
          </FormField>

          <Divider />

          <div className="flex gap">
            <FormField>
              <Checkbox
                data-testid="checkbox-call-media-group"
                label={<strong>Call Media:</strong>}
                checked={isEveryElementInGroupTrue('callMedia')}
                indeterminate={isIndeterminate('callMedia')}
                onChange={handleToggleGroup('callMedia')}
              />
            </FormField>
            <FormField>
              <Checkbox
                label="Audio"
                data-testid="checkbox-audio"
                checked={data.flagsToClear.callMedia.audio}
                onChange={handleToggle('callMedia.audio', !data.flagsToClear.callMedia.audio)}
              />
            </FormField>
            <FormField>
              <Checkbox
                label="Video"
                data-testid="checkbox-video"
                checked={data.flagsToClear.callMedia.video}
                onChange={handleToggle('callMedia.video', !data.flagsToClear.callMedia.video)}
              />
            </FormField>
            <FormField>
              <Checkbox
                label="Transcript"
                data-testid="checkbox-transcript"
                checked={data.flagsToClear.callMedia.transcript}
                onChange={handleToggle(
                  'callMedia.transcript',
                  !data.flagsToClear.callMedia.transcript
                )}
              />
            </FormField>
            <FormField>
              <Checkbox
                label="Summary"
                data-testid="checkbox-summary"
                checked={data.flagsToClear.callMedia.summary}
                onChange={handleToggle('callMedia.summary', !data.flagsToClear.callMedia.summary)}
              />
            </FormField>
          </div>

          <Divider />

          <div className="flex gap">
            <FormField>
              <Checkbox
                data-testid="checkbox-call-score-group"
                label={<strong>Call Score:</strong>}
                checked={
                  isEveryElementInGroupTrue('callScore') || data.flagsToClear.callScore.scorecards
                }
                indeterminate={isIndeterminate('callScore')}
                onChange={handleToggleGroup('callScore')}
              />
            </FormField>
            <FormField>
              <Checkbox
                label={
                  <>
                    Scorecards{' '}
                    <InteractiveTooltip
                      theme="light"
                      narrow
                      content="Deleting Scorecard data will also delete all associated Coaching and Compliance data"
                    >
                      <IconInfoCircle
                        className="icon-svg"
                        data-testid="description-icon-scorecard"
                      />
                    </InteractiveTooltip>
                  </>
                }
                data-testid="checkbox-scorecards"
                checked={data.flagsToClear.callScore.scorecards}
                onChange={() => handleToggleScorecards()}
              />
            </FormField>
            <FormField>
              <Checkbox
                label="QA Feedback"
                data-testid="checkbox-qa-feedback"
                checked={
                  data.flagsToClear.callScore.qaFeedback || data.flagsToClear.callScore.scorecards
                }
                disabled={data.flagsToClear.callScore.scorecards}
                onChange={handleToggle(
                  'callScore.qaFeedback',
                  !data.flagsToClear.callScore.qaFeedback
                )}
              />
            </FormField>
          </div>
          <Divider />

          <div className="flex gap">
            <FormField>
              <Checkbox
                data-testid="checkbox-coaching-group"
                label={<strong>Coaching:</strong>}
                checked={
                  isEveryElementInGroupTrue('coaching') || data.flagsToClear.callScore.scorecards
                }
                disabled={data.flagsToClear.callScore.scorecards}
                indeterminate={isIndeterminate('coaching')}
                onChange={handleToggleGroup('coaching')}
              />
            </FormField>
            <FormField>
              <Checkbox
                data-testid="checkbox-coaching-task"
                label="Coaching Task"
                checked={
                  data.flagsToClear.coaching.coachingTask || data.flagsToClear.callScore.scorecards
                }
                disabled={data.flagsToClear.callScore.scorecards}
                onChange={handleToggleGroup('coaching')}
              />
            </FormField>
            <FormField>
              <Checkbox
                data-testid="checkbox-coaching-note"
                label="Coaching Note"
                checked={
                  data.flagsToClear.coaching.coachingNote ||
                  data.flagsToClear.callScore.scorecards ||
                  data.flagsToClear.coaching.coachingTask
                }
                disabled={
                  data.flagsToClear.callScore.scorecards || data.flagsToClear.coaching.coachingTask
                }
                onChange={handleToggle(
                  'coaching.coachingNote',
                  !data.flagsToClear.coaching.coachingNote
                )}
              />
            </FormField>
          </div>

          <Divider />

          <div className="flex gap">
            <FormField>
              <Checkbox
                data-testid="checkbox-compliance-group"
                label={<strong>Compliance:</strong>}
                checked={
                  isEveryElementInGroupTrue('compliance') || data.flagsToClear.callScore.scorecards
                }
                disabled={data.flagsToClear.callScore.scorecards}
                indeterminate={isIndeterminate('compliance')}
                onChange={handleToggleGroup('compliance')}
              />
            </FormField>
            <FormField>
              <Checkbox
                data-testid="checkbox-compliance-task"
                label="Compliance Task"
                checked={
                  data.flagsToClear.compliance.complianceTask ||
                  data.flagsToClear.callScore.scorecards
                }
                disabled={data.flagsToClear.callScore.scorecards}
                onChange={handleToggleGroup('compliance')}
              />
            </FormField>
            <FormField>
              <Checkbox
                data-testid="checkbox-compliance-note"
                label="Compliance Note"
                checked={
                  data.flagsToClear.compliance.complianceNote ||
                  data.flagsToClear.callScore.scorecards ||
                  data.flagsToClear.compliance.complianceTask
                }
                disabled={
                  data.flagsToClear.callScore.scorecards ||
                  data.flagsToClear.compliance.complianceTask
                }
                onChange={handleToggle(
                  'compliance.complianceNote',
                  !data.flagsToClear.compliance.complianceNote
                )}
              />
            </FormField>
          </div>
        </div>
      </Card>

      <Card
        className="mb"
        title="Select Scorecard"
        subtitle="Select a scorecard to limit deletion, if left empty, scores from all scorecards will be deleted"
      >
        <FormField>
          <MultiSelect
            isSelectAll
            fixedWidth
            placeholderPill="No Scorecards selected"
            options={scorecardOptions}
            aria-label="Scorecards"
            loading={callSearchLoading.scorecards}
            value={filters.scorecardConfigsToDelete}
            onChange={(value) => dispatch(setFilters({ scorecardConfigsToDelete: value }))}
          />
        </FormField>
      </Card>
      <Button
        data-testid="submit-button"
        brand
        onClick={handleSubmit}
        disabled={isEmpty(data.calls) || loading.calls || areNoFlagsSelected}
        loading={loading.calls}
      >
        Submit
      </Button>
    </>
  )
}

export default DataCleanupPage
